import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import {
  User, Calendar, RefreshCw, CheckCircle, XCircle,
  AlertTriangle, ArrowRight, Clock, Receipt,
} from "lucide-react";
import { toast } from "sonner";

function formatDate(isoString) {
  if (!isoString) return "—";
  return new Date(isoString).toLocaleDateString("nb-NO", {
    day: "numeric", month: "long", year: "numeric",
  });
}

function daysLeft(isoString) {
  if (!isoString) return 0;
  const diff = new Date(isoString) - new Date();
  return Math.max(0, Math.ceil(diff / 86400000));
}

export default function Account() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [subRes, ordersRes] = await Promise.all([
          api.get("/payment/subscription"),
          api.get("/payment/orders"),
        ]);
        setSub(subRes.data);
        setOrders(ordersRes.data);
      } catch (_) {}
      setLoading(false);
    };
    load();
  }, []);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const res = await api.post("/payment/subscription/cancel");
      toast.success("Abonnement kansellert. Du beholder tilgangen til " + formatDate(res.data.access_until));
      setShowCancelConfirm(false);
      // Refresh subscription status
      const subRes = await api.get("/payment/subscription");
      setSub(subRes.data);
      if (refresh) await refresh();
    } catch (err) {
      const msg = err.response?.data?.detail || "Kunne ikke kansellere. Kontakt support@veiklar.no";
      toast.error(msg);
    } finally {
      setCancelling(false);
    }
  };

  const isActive    = sub?.active;
  const autoRenews  = sub?.auto_renews;
  const isCancelled = sub?.subscription_status === "CANCELLED";
  const days        = daysLeft(sub?.subscribed_until);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 py-12">
        <h1 className="text-2xl font-bold text-gray-900 mb-8">Min konto</h1>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-6 h-6 border-2 border-fjord border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Profile card */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-fjord/10 text-fjord grid place-items-center">
                  <User className="w-6 h-6" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">{user?.name || "—"}</div>
                  <div className="text-sm text-gray-500">{user?.email || user?.phone || "—"}</div>
                </div>
              </div>
            </div>

            {/* Subscription card */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6">
              <h2 className="font-semibold text-gray-900 mb-5">Abonnement</h2>

              {isActive ? (
                <div className="space-y-4">
                  {/* Status banner */}
                  <div className={`flex items-center gap-3 p-4 rounded-xl ${autoRenews && !isCancelled ? "bg-green-50 border border-green-100" : "bg-blue-50 border border-blue-100"}`}>
                    <CheckCircle className={`w-5 h-5 shrink-0 ${autoRenews && !isCancelled ? "text-green-600" : "text-blue-600"}`} />
                    <div>
                      <div className={`font-semibold text-sm ${autoRenews && !isCancelled ? "text-green-800" : "text-blue-800"}`}>
                        {autoRenews && !isCancelled ? "Aktivt abonnement · fornyes automatisk" : "Aktiv · manuell fornyelse"}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {days > 0 ? `${days} dager igjen` : "Utløper i dag"}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-xl p-4">
                      <div className="text-xs text-gray-400 mb-1 flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5" /> Tilgang til
                      </div>
                      <div className="text-sm font-semibold text-gray-800">{formatDate(sub?.subscribed_until)}</div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4">
                      <div className="text-xs text-gray-400 mb-1 flex items-center gap-1.5">
                        <RefreshCw className="w-3.5 h-3.5" /> Neste betaling
                      </div>
                      <div className="text-sm font-semibold text-gray-800">
                        {autoRenews && !isCancelled ? formatDate(sub?.next_charge_at || sub?.subscribed_until) : "Ingen"}
                      </div>
                    </div>
                  </div>

                  {/* Cancel / renew actions */}
                  {autoRenews && !isCancelled ? (
                    <div>
                      {!showCancelConfirm ? (
                        <button
                          onClick={() => setShowCancelConfirm(true)}
                          className="text-sm text-red-500 hover:text-red-700 underline underline-offset-2 transition-colors"
                        >
                          Kanseller abonnement
                        </button>
                      ) : (
                        <div className="bg-red-50 border border-red-100 rounded-xl p-4">
                          <div className="flex items-start gap-2 mb-3">
                            <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                            <div className="text-sm text-red-800">
                              <strong>Er du sikker?</strong> Abonnementet kanselleres og du vil ikke bli belastet igjen. Du beholder tilgangen til {formatDate(sub?.subscribed_until)}.
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={handleCancel}
                              disabled={cancelling}
                              className="flex-1 bg-red-600 text-white text-sm font-semibold rounded-lg py-2 hover:bg-red-700 transition-colors disabled:opacity-50"
                            >
                              {cancelling ? "Kansellerer…" : "Ja, kanseller"}
                            </button>
                            <button
                              onClick={() => setShowCancelConfirm(false)}
                              className="flex-1 border border-gray-200 text-sm text-gray-700 rounded-lg py-2 hover:border-gray-300 transition-colors"
                            >
                              Behold abonnement
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : isCancelled ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 rounded-xl p-3">
                      <XCircle className="w-4 h-4 text-gray-400" />
                      Kansellert · tilgang gjelder til {formatDate(sub?.subscribed_until)}
                    </div>
                  ) : null}

                  {!autoRenews && !isCancelled && (
                    <button
                      onClick={() => navigate("/checkout")}
                      className="w-full bg-fjord text-white text-sm font-semibold rounded-xl py-2.5 hover:bg-fjord/90 transition-colors flex items-center justify-center gap-2"
                    >
                      Forny abonnement <ArrowRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 rounded-xl bg-gray-50 border border-gray-200">
                    <Clock className="w-5 h-5 text-gray-400 shrink-0" />
                    <div>
                      <div className="font-semibold text-sm text-gray-700">Ingen aktiv tilgang</div>
                      <div className="text-xs text-gray-400 mt-0.5">Få full tilgang for 79 kr/måned</div>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate("/checkout")}
                    className="w-full bg-fjord text-white font-semibold rounded-xl py-3 hover:bg-fjord/90 transition-colors flex items-center justify-center gap-2"
                  >
                    Abonner nå — 79 kr/mnd <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            {/* Payment history */}
            {orders.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Receipt className="w-4 h-4 text-gray-400" /> Betalingshistorikk
                </h2>
                <div className="space-y-3">
                  {orders.map((o) => (
                    <div key={o.order_id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                      <div>
                        <div className="text-sm font-medium text-gray-800">VeiKlar månedstilgang</div>
                        <div className="text-xs text-gray-400 mt-0.5">{formatDate(o.paid_at || o.created_at)}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          o.status === "paid" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
                        }`}>
                          {o.status === "paid" ? "Betalt" : o.status}
                        </span>
                        <span className="text-sm font-semibold text-gray-900">
                          {((o.amount_ore || 7900) / 100).toFixed(0)} kr
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Danger zone */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6">
              <button
                onClick={logout}
                className="text-sm text-red-500 hover:text-red-700 transition-colors"
              >
                Logg ut
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
